/**
 * Public deep-link to the ORIGINAL tender on the source platform, computed from
 * `(sourceKey, externalId)` — no stored URL needed. external_ids are namespaced
 * per source (`mc-`, `civil-`, `auc-`, `prequest-`, `ted:`, `ungm-`, `undp-`,
 * `trustbank-`, `adb-`, `coop-`, `coop-auc-`, `coop-locauc-`); we strip the
 * prefix and route to the source's public page template.
 *
 * All route patterns below were confirmed against the live sites (browser load
 * of a real lot) — see docs/reports. Sources/sub-types without a confirmed route
 * return null (no link) rather than a dead link.
 *
 * ⚠️ The `(sourceKey, externalId)` pair is itself PRO-ONLY, for every layer —
 * not just `ifi`. On the origin portal's own lot page the заказчик contacts we
 * sell behind the Pro wall are published for free, so handing the pair to an
 * anonymous caller advertised the paywall's own bypass. The gate lives at the
 * data layer, not here: `catalog.service.ts` (`sourceRef`) OMITS both keys from
 * the list + detail views unless `EntitlementService.isPro` says otherwise
 * — on every layer, `ifi` included. This function therefore only ever sees real data for an
 * entitled caller, and returns null when the pair is absent — callers must
 * still keep the rendered link behind their own `unlocked &&` check. See
 * `docs/explanation/tender-detail-entitlements.md`.
 */

/** Strip a known namespace prefix from an external id → the source's own id.
 *  Exported so the catalog card can show the bare procurement number. */
export function bareId(externalId: string): string {
  return externalId.replace(
    // `coop-locauc-`/`coop-auc-` must be tried before the bare `coop-`
    // prefix (the cooperation-auction namespace), or the shorter `coop-`
    // alternative would match first and leave a stray `locauc-`/`auc-` on
    // the id.
    /^(mc-|civil-|auc-|prequest-|ted:|ungm-|undp-|trustbank-|adb-|coop-locauc-|coop-auc-|coop-|getbiz-|ucell-|b2biz-|saneg-|uzairports-|uzgidroqurilish-|uzkor-|farma-)/,
    '',
  );
}

export function sourceTenderUrl(
  sourceKey: string | null | undefined,
  externalId: string | null | undefined,
): string | null {
  if (!sourceKey || !externalId) return null;
  const id = bareId(externalId);
  if (!id) return null;

  switch (sourceKey) {
    // Ministry of Construction — route + storage host verified.
    case 'tender-mc':
      return `https://tender.mc.uz/tender-list/tender/${id}/view`;

    // etender: TradeList lots vs CivilContracts lots have distinct routes,
    // distinguished by the `civil-` external-id prefix. Both confirmed live.
    case 'etender-uzex':
      return externalId.startsWith('civil-')
        ? `https://etender.uzex.uz/civil-detail/${id}`
        : `https://etender.uzex.uz/lot/${id}`;

    // xarid competitions — the non-auction subsystem. Confirmed 2026-09-04:
    // these lots are COMPETITIONS, served at `/purchase/competition/detail/<id>`
    // (its API `GetCompetition/<id>` returns the lot). The old
    // `/procurement-announcement/<id>/details` was wrong — its API
    // `GetProcAnnouncementDetails/<id>` 500s with `ORA-01403 no data found`
    // for these lots (10/10 real live ids checked from a UZ IP).
    // Auctions (`auc-`) are a separate subsystem on the SAME host — confirmed
    // 2026-08-19 by loading five real live lots (e.g. 412181 → «Оборудование
    // компьютерное, электронное и оптическое», № лота 26111007412181); each
    // page's `№ лота` ends in the requested id. The `/detail/` segment is
    // load-bearing — bare `/auction/<id>` silently redirects to the homepage.
    case 'uzex-xarid':
      return externalId.startsWith('auc-')
        ? `https://xarid.uzex.uz/auction/detail/${id}`
        : `https://xarid.uzex.uz/purchase/competition/detail/${id}`;

    // xt-xarid (MoF) — confirmed live.
    case 'xt-xarid':
      return `https://xt-xarid.uz/procedure/${id}/core`;

    // UzEx So'rov-takliflar (RFP) — route confirmed from the SPA bundle.
    case 'xarid-prequest':
      return `https://xarid.uzex.uz/request-proposal/3/detail/${id}`;

    // UNGM (UN Global Marketplace) — confirmed live 2026-07-23: fetched a real
    // page of Uzbekistan notices from the source's own search endpoint and
    // loaded `/Public/Notice/<id>` for two of the returned notice ids; both
    // returned 200 with a `<title>` matching the notice's own title.
    case 'ungm':
      return `https://www.ungm.org/Public/Notice/${id}`;

    // UNDP procurement-notices — confirmed live 2026-07-23 against a real
    // Uzbekistan row from the source's own listing page. The query param is
    // `nego_id`, NOT `nav_id` — an earlier `nav_id` guess 404s; `nego_id` loads
    // the notice with a matching <title>. (The listing also carries a second,
    // unrelated `notice_id`/`view_notice.cfm` row family the adapter never
    // maps into `undp-<id>` external ids — see undp.adapter.ts — so it needs
    // no route here.)
    case 'undp':
      return `https://procurement-notices.undp.org/view_negotiation.cfm?nego_id=${id}`;

    // Trustbank — confirmed live 2026-07-23 against the two real rows on
    // `/uz/press_center/tenders/current/`. Only the `uz` locale *with* the
    // `/current/` segment resolves (200); `/ru/...` and dropping `/current/`
    // both 404 — a guessed `ru` path (as an initial pattern draft assumed)
    // would have been a dead link.
    case 'trustbank':
      return `https://trustbank.uz/uz/press_center/tenders/current/${id}/`;

    // ADB — the Drupal node id embedded in the Solr doc id (e.g.
    // `3y8wre-tender_search_index-entity:node/865986:en`). Confirmed live
    // 2026-07-23: `www.adb.org` 403s a plain server-side curl (Akamai), but a
    // browser-UA request resolves `/node/<id>` (301 → the notice's own PDF, or
    // for consultant notices, ADB's self-service application portal) — real
    // Drupal 10 + Varnish/Cloudflare response headers, not a generic error
    // page. Cross-checked against ADB's own live Solr data: every doc that
    // also carries `ss_url` (ADB's own "link to this notice" field) has it
    // equal exactly `/node/<id>` — confirming `/node/<id>` is ADB's own
    // canonical notice URL, not a guess.
    case 'adb': {
      const m = id.match(/node\/(\d+)/);
      return m ? `https://www.adb.org/node/${m[1]}` : null;
    }

    // World Bank procurement notices — confirmed live 2026-07-23. The detail
    // page is a client-rendered shell for every id (so a plain fetch can't
    // read notice text off it directly), but its own embedded `titleUrl`
    // wires to `search.worldbank.org/api/procnotices?...&id=<id>` — the SAME
    // API the adapter itself reads — and a real id vs. a bogus one produce
    // genuinely different results from that API (`total:1` with the correct
    // `project_name` vs. `total:0`), proving the route resolves per-notice
    // rather than just returning a static 200 shell regardless of id.
    case 'worldbank':
      return `https://projects.worldbank.org/en/projects-operations/procurement-detail/${id}`;

    // AIIB — no confirmed route. The project-procurement listing is rendered
    // entirely client-side from a static `ppoData` JS blob with no per-notice
    // HTML page or URL fragment anywhere on the site. The one quasi-unique
    // field (`dc`, a PDF download path) can't be reconstructed from here: the
    // stored external id keeps only the trailing filename slug (see
    // aiib.adapter.ts), discarding the year/country path segments the full
    // download URL needs — there's nothing left to route on. Deliberately
    // returns null rather than guess.
    case 'aiib':
      return null;

    // new.cooperation.uz supply requests (scope='supply'). `mapCoopLot`
    // namespaces `coop-<offerNumber ?? lotNumber>`; in the live data that is the
    // OFFER number «O…» (5,002 rows) — the lot number «SL…» is a different id on
    // the same row. The old `/lot/<id>` route never existed on the portal (their
    // own 404 page — checked from a UZ IP 2026-09-07, see
    // /home/analyst/cooperation-link-format.md). Verified routes:
    //   «O…»  → /e-catalog/<category>/<O…>  — the category segment is ignored by
    //            the portal (1, 10 and 11 all render the same offer), so a
    //            constant is enough and nothing new has to be stored;
    //   «M1…» → /e-shop/<digits after the M1> (e-shop cards show M1204512, the
    //            page is /e-shop/204512).
    // «SL…» lots and the auction/tender/selection sections had no live rows to
    // verify against, so those get NO link rather than a guessed 404.
    case 'cooperation': {
      if (/^O\d+$/.test(id)) return `https://new.cooperation.uz/e-catalog/10/${id}`;
      const shop = /^M1(\d+)$/.exec(id);
      if (shop) return `https://new.cooperation.uz/e-shop/${shop[1]}`;
      return null;
    }

    // cooperation.uz LIVE reverse-auction lots (distinct from the dropped
    // `cooperation` supply-request source above). `mapCoopAuction` namespaces
    // `coop-auc-<id>` (auction) vs `coop-locauc-<id>` (local-auction) — the
    // `/auction/<id>` public route is confirmed live (recon rendered lot 709
    // at new.cooperation.uz/auction/709); `/local-auction/<id>` is the
    // symmetric route for a `PLocalAuctionDetail` SPA page that exists on the
    // site, but has NOT been live-verified against a real id — an assumption,
    // not a confirmed route like the others in this switch.
    case 'cooperation-auction':
      return externalId.startsWith('coop-locauc-')
        ? `https://new.cooperation.uz/local-auction/${id}`
        : `https://new.cooperation.uz/auction/${id}`;

    default:
      return null;
  }
}
